import { DependencyList, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

type UsePresetNameDescriptionFormArgs = {
  // Both dialogs stay mounted while the parent is open, so the form must re-seed
  // whenever it (re)opens.
  open: boolean;
  // Values to seed the fields with on open. SaveAs seeds blanks; Edit seeds the
  // preset's current name/description.
  initialName: string;
  initialDescription: string;
  // Extra dependencies that should also re-seed the fields (e.g. preset.id so
  // opening the Edit dialog for a different preset refreshes the fields).
  seedDeps?: DependencyList;
  // The persistence step (create/update). It also owns the success side effects
  // (store update, onSaved, onClose) so each dialog keeps its distinct bits.
  onSave: (values: { name: string; description: string }) => Promise<void>;
  // Distinct log/toast copy per dialog.
  logMessage: string;
  toastMessage: string;
};

// Shared scaffolding for the "name + description" preset dialogs
// (SaveAsPresetDialog, EditPresetDialog): name/description/saving state, the
// open-guarded re-seed effect, and a handleSave that wraps the persistence call
// in try/setSaving/catch+toast/finally. `canSave` is the single source of truth
// for the name+description validation both dialogs require.
export const usePresetNameDescriptionForm = ({
  open,
  initialName,
  initialDescription,
  seedDeps = [],
  onSave,
  logMessage,
  toastMessage,
}: UsePresetNameDescriptionFormArgs) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [open, ...seedDeps]);

  // Both dialogs require a non-empty name and description.
  const canSave = !!name.trim() && !!description.trim();

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ name: name.trim(), description: description.trim() });
    } catch (error) {
      console.error(logMessage, error);
      toast.error(toastMessage);
    } finally {
      setSaving(false);
    }
  };

  return { name, setName, description, setDescription, saving, canSave, handleSave };
};
